/* assets/quick-add-variant-highlight.js */
/* Multi-select highlight that persists across AJAX & reloads; robust product-key detection */

(function () {
  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();

  onReady(() => {
    const DEBUG = true; // set to false after verifying
    const log = (...a) => DEBUG && console.log('[QA Highlight]', ...a);
    log('init (multi + persistent + robust from button/toolbar)');

    const STORAGE_KEY = 'qaSelectedVariantIds:v1';

    const SEL = {
      btn: '[data-add-to-cart][data-variant-id]',
      toolbar: '[id^="quick-add-menu-toolbar"], [role="toolbar"]',
      card: '[data-product-card], .product-card, .grid-product, .card--product, [data-quick-add]'
    };

    const getToolbar = (el) => el.closest(SEL.toolbar);

    // Try very hard to get a stable product key (prefer id)
    function deriveProductKeyFromAny(el) {
      if (!el) return null;

      // 1) Directly on the clicked button
      if (el.dataset?.qaProductId) {
        return { type: 'id', key: el.dataset.qaProductId, source: 'btn:data-qa-product-id' };
      }
      if (el.dataset?.productId) {
        return { type: 'id', key: el.dataset.productId, source: 'btn:data-product-id' };
      }

      // 2) On its toolbar
      const tb = getToolbar(el);
      if (tb?.dataset?.qaProductId) {
        return { type: 'id', key: tb.dataset.qaProductId, source: 'toolbar:data-qa-product-id' };
      }
      if (tb?.dataset?.productId) {
        return { type: 'id', key: tb.dataset.productId, source: 'toolbar:data-product-id' };
      }

      // 3) Walk up for common product wrappers
      const card = el.closest(SEL.card) || tb?.closest?.(SEL.card);
      if (card?.dataset?.qaProductId) {
        return { type: 'id', key: card.dataset.qaProductId, source: 'card:data-qa-product-id' };
      }
      if (card?.dataset?.productId) {
        return { type: 'id', key: card.dataset.productId, source: 'card:data-product-id' };
      }
      if (card?.dataset?.productHandle) {
        return { type: 'handle', key: card.dataset.productHandle, source: 'card:data-product-handle' };
      }

      // 4) Link-based fallback (/products/<handle>)
      const link = (card || document).querySelector('a[href*="/products/"]');
      if (link) {
        try {
          const u = new URL(link.href, location.origin);
          const parts = u.pathname.split('/').filter(Boolean);
          const i = parts.indexOf('products');
          if (i !== -1 && parts[i + 1]) {
            return { type: 'handle', key: parts[i + 1], source: 'link:/products/<handle>' };
          }
        } catch {}
      }

      // 5) PDP meta fallback
      const og = document.querySelector('meta[property="og:url"]')?.content;
      if (og && og.includes('/products/')) {
        try {
          const u = new URL(og, location.origin);
          const parts = u.pathname.split('/').filter(Boolean);
          const i = parts.indexOf('products');
          if (i !== -1 && parts[i + 1]) {
            return { type: 'handle', key: parts[i + 1], source: 'og:url' };
          }
        } catch {}
      }

      log('⚠️ could not derive product key; add data-qa-product-id to button/toolbar');
      return null;
    }

    // storage helpers
    const loadStore = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : { byId: {}, byHandle: {} };
      } catch {
        return { byId: {}, byHandle: {} };
      }
    };
    const saveStore = (obj) => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); } catch {}
    };

    const getSelectedSet = (toolbar) => {
      const raw = toolbar?.dataset?.qaSelectedVariantIds || '';
      const live = new Set(raw.split(',').map(s => s.trim()).filter(Boolean));
      if (live.size) return live;

      // fallback to storage by product key
      const key = deriveProductKeyFromAny(toolbar);
      if (!key) return new Set();
      const store = loadStore();
      const arr = key.type === 'id' ? store.byId[key.key] || [] : store.byHandle[key.key] || [];
      return new Set(arr);
    };

    const writeSelectedSet = (contextEl, toolbar, set) => {
      toolbar.dataset.qaSelectedVariantIds = [...set].join(',');
      const key = deriveProductKeyFromAny(contextEl);
      if (!key) return;

      const store = loadStore();
      if (key.type === 'id') store.byId[key.key] = [...set];
      else store.byHandle[key.key] = [...set];
      saveStore(store);
      log('saved', key, [...set]);
    };

    const paint = (toolbar, set) => {
      toolbar.querySelectorAll(`${SEL.btn}.is-selected`).forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      set.forEach((id) => {
        const btn = toolbar.querySelector(`${SEL.btn}[data-variant-id="${CSS.escape(id)}"]`);
        if (btn) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
        }
      });
    };

    const addSelection = (btn) => {
      const toolbar = getToolbar(btn);
      if (!toolbar) return;
      const id = btn.dataset.variantId;
      const set = getSelectedSet(toolbar);
      set.add(id);
      writeSelectedSet(btn, toolbar, set);
      paint(toolbar, set);
    };

    const restoreAll = () => {
      document.querySelectorAll(SEL.toolbar).forEach((tb) => {
        const set = getSelectedSet(tb);
        if (set.size) {
          log('restore →', deriveProductKeyFromAny(tb), [...set]);
          paint(tb, set);
        }
      });
    };

    // select on pointerdown (fires before AJAX loading state swaps)
    document.addEventListener('pointerdown', (e) => {
      const btn = e.target.closest(SEL.btn);
      if (!btn || btn.disabled) return;
      addSelection(btn);
    });

    // re-apply after DOM swaps
    const mo = new MutationObserver((muts) => {
      const seen = new Set();
      for (const m of muts) {
        const el = m.target && m.target.nodeType === 1 ? m.target : null;
        if (!el) continue;
        const tb = el.matches?.(SEL.toolbar) ? el : el.closest?.(SEL.toolbar);
        if (tb && !seen.has(tb)) {
          seen.add(tb);
          const set = getSelectedSet(tb);
          if (set.size) paint(tb, set);
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    restoreAll();
    document.addEventListener('shopify:section:load', restoreAll);
  });
})();
